import { useState, useMemo, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useToast } from '@/hooks/useToast';
import { extractErrorMessage } from '@/hooks/useErrorHandler';
import type { IndustryType } from '@/lib/api/auth';

// Auto-detect the operator's IANA timezone via the browser. Falls back to
// "UTC" on the rare browser that doesn't return a resolved zone (some old
// Android WebViews, locked-down corporate browsers). Memoised so the
// useMemo dependency below is referentially stable across re-renders.
const detectTimezone = (): string => {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz || 'UTC';
  } catch {
    return 'UTC';
  }
};

// Mirrors backend's PasswordMixin rule. We pre-validate client-side so a
// failed register doesn't burn a network round trip, but the server still
// enforces this — these flags are a UX nicety, not a security control.
export interface PasswordChecks {
  length: boolean;
  digit: boolean;
  upper: boolean;
  lower: boolean;
}

const checkPassword = (pw: string): PasswordChecks => ({
  length: pw.length >= 8,
  digit: /\d/.test(pw),
  upper: /[A-Z]/.test(pw),
  lower: /[a-z]/.test(pw),
});

const allPasswordChecksPass = (c: PasswordChecks): boolean =>
  c.length && c.digit && c.upper && c.lower;

export const useSignupForm = () => {
  const signup = useAuthStore((s) => s.signup);
  const isLoading = useAuthStore((s) => s.isLoading);
  const navigate = useNavigate();
  const toast = useToast();

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [workEmail, setWorkEmail] = useState('');
  const [company, setCompany] = useState('');
  const [industryType, setIndustryType] = useState<IndustryType>('office');
  // IANA timezone resolved once at mount. Used at signup so the backend
  // can render emails/reports in the user's local time even when the
  // browser isn't around. The user can override via the chip on the form.
  const [timezone, setTimezone] = useState<string>(() => detectTimezone());
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [localError, setLocalError] = useState<string | null>(null);

  // For the form's "Detected: <tz>" chip. Stable across re-renders.
  const detectedTimezone = useMemo(detectTimezone, []);

  const passwordChecks = checkPassword(password);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setLocalError(null);

    // Client-side validation — backend will re-validate.
    if (firstName.trim().length < 2 || lastName.trim().length < 2) {
      setLocalError('First and last name must be at least 2 characters each.');
      return;
    }
    if (company.trim().length < 2) {
      setLocalError('Company name must be at least 2 characters.');
      return;
    }
    if (!allPasswordChecksPass(passwordChecks)) {
      setLocalError('Password does not meet the requirements below.');
      return;
    }
    if (password !== confirmPassword) {
      setLocalError('Passwords do not match.');
      return;
    }
    if (!termsAccepted) {
      setLocalError('You must accept the terms and conditions to continue.');
      return;
    }

    try {
      await signup({
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        work_email: workEmail.trim().toLowerCase(),
        company: company.trim(),
        industry_type: industryType,
        // Send whatever the user confirmed (or the auto-detected fallback).
        // The backend re-validates as IANA — typos like "Asia/Kolkat" get
        // rejected with a clear error.
        timezone: timezone || undefined,
        password,
        terms_accepted: termsAccepted,
      });
      // Backend issued tokens + user is set in store -> route into the app.
      toast.success('Account created. Welcome to ProWatchly.');
      navigate('/dashboard', { replace: true });
    } catch (err) {
      const msg = extractErrorMessage(err);
      setLocalError(msg);
      toast.error(msg || 'Sign-up failed.');
    }
  };

  return {
    firstName, setFirstName,
    lastName, setLastName,
    workEmail, setWorkEmail,
    company, setCompany,
    industryType, setIndustryType,
    timezone, setTimezone,
    detectedTimezone,
    password, setPassword,
    confirmPassword, setConfirmPassword,
    termsAccepted, setTermsAccepted,
    passwordChecks,
    isLoading,
    error: localError,
    onSubmit,
  };
};
